/**
 * PreparationDragController — drag-and-drop reordering for the flat
 * preparation-step sequence (the Elaboració tab's numbered rows).
 *
 * Pointer/touch mechanics mirror list_view/ListDragController's (manual
 * mousedown/touchmove tracking, auto-scroll near the list's edges, and
 * the touch long-press-vs-scroll disambiguation — see that file's own
 * doc comment for the full rationale on each of those). The resolution
 * logic is much simpler, though: this is a flat list, not a tree, so
 * there's no reparent/orphan, no "onto" zone — just "insert before/after
 * the hovered row".
 *
 * Unlike ListDragController's floating ghost-clone-follows-cursor, this
 * controller moves the REAL rows: dragging is constrained to vertical
 * movement only (there's no horizontal ordering concept in a flat list),
 * and on every move it swaps with whichever neighbor the floating
 * group's leading edge has pushed past the midpoint of (see
 * resolveSwapAnchor()), asking the caller (via `previewOrder`) what the
 * FULL resulting order would be if dropped right now, AND which other
 * node ids the dependency cascade would drag along with it. Those
 * cascaded nodes get LIFTED out of flow too — attached to the dragged
 * block, growing it, rather than just reflowing in place — so passing
 * over a dependency visibly picks it up along with the dragged node.
 * Everyone else reorders via a FLIP transform transition. A drop
 * commits the same move for real via `onDrop`.
 *
 * Both callbacks are anchor-based — "insert before/after this specific
 * OTHER rendered node" — rather than a raw numeric index. This list only
 * ever renders a SUBSET of the recipe's nodes (prep-step-eligible ones;
 * ingredients/tools have no row here), so "how many rendered rows sit
 * above the cursor" is NOT a valid index into the full recipe's node
 * list RecipeStore.moveNodeToStepOrder() actually operates on — it's an
 * index into a shorter, different sequence. Naming a concrete rendered
 * sibling to anchor against sidesteps that mismatch entirely: the
 * caller (RecipeStore.resolveStepOrderIndexRelativeTo()) resolves it
 * against the real full list, however many unrendered nodes happen to
 * fall between rendered ones.
 *
 * A node with several transformations renders several consecutive rows
 * (same data-node-id) — dragging any one of them drags the whole node
 * (and so its whole block of rows), since moveNodeToStepOrder() operates
 * per node, not per transformation usage (a node's own transformations
 * stay in their existing relative order; that's not draggable — see the
 * "Explicit, draggable step order" plan).
 */

export interface PreparationDragCallbacks {
  /** Fired whenever a drag starts or ends (past the movement threshold) — call render() unconditionally here; the caller's own store-subscription render should separately skip re-rendering while isActive() is true, same split ListView uses with its own ListDragController. */
  onDragStateChange: (dragging: boolean) => void;
  /**
   * Read-only live preview: given a hypothetical drop placing `nodeId`
   * immediately before/after `anchorNodeId` (both rendered-row node
   * ids), the full resulting node-id order plus whichever OTHER node
   * ids the dependency cascade forces to move alongside `nodeId`
   * (RecipeStore.previewStepOrderCascaded()). `anchorNodeId` is only
   * ever null in the degenerate case of `nodeId` being the sole
   * rendered row. Called on every resolved pointer move, never mutates
   * anything.
   */
  previewOrder: (nodeId: string, anchorNodeId: string | null, insertBefore: boolean) => { order: string[]; cascadedIds: string[] };
  /** Fired with the resolved move on a valid drop — same (nodeId, anchorNodeId, insertBefore) shape as previewOrder. */
  onDrop: (nodeId: string, anchorNodeId: string | null, insertBefore: boolean) => void;
}

/** One currently-lifted (out-of-flow, position: fixed) node's rows. */
interface LiftedBlock {
  rows: HTMLElement[];
  /** Each row's top relative to the block's own first row's top — rigid, frozen at lift time. */
  rowTopOffsets: number[];
  /** Last row's bottom minus first row's top. */
  blockHeight: number;
}

const DRAG_THRESHOLD = 5;
const FLIP_DURATION_MS = 150;
const AUTOSCROLL_MARGIN = 48;
const AUTOSCROLL_SPEED = 7;
const TOUCH_LONG_PRESS_MS = 200;
const TOUCH_LONG_PRESS_SLOP = 10;

export class PreparationDragController {
  private callbacks: PreparationDragCallbacks;

  private startX = 0;
  private startY = 0;
  private dragging = false;
  private draggedNodeId: string | null = null;
  private draggedRowEl: HTMLElement | null = null;
  // The nearest ancestor that actually scrolls (real overflow-y:auto/
  // scroll AND scrollHeight > clientHeight), found by findScrollableAncestor()
  // at drag(-attempt) start — or `window` if none exists up to <body>,
  // e.g. on a wide layout where the page itself scrolls natively. NOT
  // simply "whichever container the row was rendered into": on the
  // narrow/mobile layout specifically, that container (the Preparació
  // panel div) has no overflow of its own at all — the real scrolling
  // ancestor further up is `#home-elaboracio-panel` — so scrolling IT
  // directly was a silent no-op.
  private scrollTarget: HTMLElement | Window | null = null;

  // Frozen for the duration of one drag gesture, built once in
  // beginDragVisuals() — NOT re-derived from the (possibly already
  // live-reordered) DOM mid-drag. Map insertion order is the recipe's
  // original stepOrder sequence.
  private listContainer: HTMLElement | null = null;
  private rowsByNodeId: Map<string, HTMLElement[]> | null = null;
  // Whatever element immediately precedes the first `.preparation-step-row`
  // within listContainer (e.g. the panel's own "Preparació" header — it's
  // rendered as a sibling of the rows, not a separate container) — null
  // if a row is already listContainer's first child. applyPreviewOrder()
  // anchors "insert as the very first row" relative to THIS, never to
  // listContainer.firstChild directly: that would (and did) displace
  // whatever non-row element already sits at the front on every reflow.
  private beforeRowsAnchor: HTMLElement | null = null;

  // Every node currently lifted out of flow — always includes
  // draggedNodeId, plus whatever the live cascade is currently pulling
  // along with it (grows/shrinks as the drag passes over, or away from,
  // a dependency boundary). All of them are "attached" to the same
  // floating block and stacked together — see layoutLiftedStack().
  private liftedNodeIds: Set<string> = new Set();
  private liftedBlocks: Map<string, LiftedBlock> = new Map();
  // The dragged node's own block top at the moment it was lifted
  // (drag start) — the one absolute anchor everything else in the
  // floating stack is positioned relative to, via currentDeltaY.
  private draggedBlockOriginalTop = 0;
  // The dragged block's own original vertical CENTER — combined with
  // currentDeltaY, gives the block's live on-screen center, which is
  // what resolveHover() compares against other (still-in-flow) rows
  // (NOT the raw cursor point): the dashed gap has to react to wherever
  // the block ITSELF visually is, which only equals the raw cursor
  // point if the user happened to grab it exactly at its center. Kept
  // live (not just an original, frozen value) by layoutLiftedStack() on
  // every tick: when a dependency attaches/detaches mid-drag, the
  // group's actual extent changes, and resolveHover()'s comparison has
  // to track that change in the SAME tick it happens — comparing
  // against a reference that still only reflected the dragged node's
  // own (unchanged) position caused a feedback loop (attaching a
  // dependency shifts the remaining in-flow rows, which the next tick's
  // stale-centered comparison would then read as "undo the attach",
  // which shifts them back, which re-triggers the attach, ad infinitum
  // — visible as rapid flicker right at a cascade boundary).
  private groupTop = 0;
  private groupBottom = 0;
  private currentDeltaY = 0;
  // Most recently resolved full hypothetical order (RecipeStore.
  // previewStepOrderCascaded()'s `order`) — cached so updateDraggedPosition()
  // can keep the floating stack's internal sequencing stable between
  // resolveHover() ticks (which only recompute it when the target
  // actually changes).
  private lastOrder: string[] = [];
  // A dashed-outline gap left behind in flow, standing in for the WHOLE
  // lifted group (dragged node + any attached cascaded dependencies) in
  // the FLIP reflow below — this is what actually shows "it will land
  // here", sized to match everything currently lifted, and what the
  // surrounding rows visibly separate for.
  private placeholderEl: HTMLElement | null = null;
  // Each still-in-flow node's current LOGICAL top/bottom — i.e. where
  // it truly is/will settle, ignoring any FLIP transform transition
  // currently animating it. resolveSwapAnchor() reads ONLY this, never
  // a live getBoundingClientRect() on another row directly: that
  // reflects whatever's currently PAINTED, which — mid-transition — is
  // an interpolating value, not the row's true position. Reorders land
  // well within FLIP_DURATION_MS of each other while actively dragging
  // (mousemove fires far faster than 150ms), so without this, most
  // comparisons were being made against a moving target purely because
  // of elapsed time, not actual further cursor movement — the "timing
  // issue" behind the remaining flicker. Updated authoritatively by
  // applyPreviewOrder() exactly when it moves something, using a
  // reading taken before any transform is applied for that move.
  private settledRect: Map<string, { top: number; bottom: number }> = new Map();

  private pendingAnchor: { anchorNodeId: string | null; insertBefore: boolean } | null = null;
  private lastResolvedKey: string | null = null;

  private autoScrollDir: -1 | 0 | 1 = 0;
  private autoScrollRAF: number | null = null;

  constructor(callbacks: PreparationDragCallbacks) {
    this.callbacks = callbacks;
  }

  /** Whether a drag is currently in progress (past the movement threshold). */
  isActive(): boolean {
    return this.dragging;
  }

  /** If a drag is in progress, abort it with no effect. Call before a re-render replaces the DOM out from under it. */
  cancel(): void {
    this.cleanup();
  }

  /**
   * Wire drag-start listeners on a rendered step row's dedicated drag
   * handle — NOT the row itself. Call for every `.preparation-step-row`
   * on every render. `handleEl` is the row's own
   * `.preparation-step-drag-handle` (buildPreparationHtml()'s only
   * edit-mode-conditional markup): the row's other content stays plain,
   * ordinary-touch-scrollable, with no drag affordance at all — the
   * whole row used to be the drag target, which on mobile meant any
   * scroll swipe over a step could be mistaken for (or had to fight
   * against) starting a drag.
   */
  registerRow(rowEl: HTMLElement, handleEl: HTMLElement, nodeId: string): void {
    const start = (clientX: number, clientY: number): void => {
      this.startX = clientX;
      this.startY = clientY;
      this.dragging = false;
      this.draggedNodeId = nodeId;
      this.draggedRowEl = rowEl;
      // Resolved here (on every press attempt, not just once a real
      // drag starts) since the touch path's own manual-scroll fallback
      // — see the 'scrolling' mode below — can need it before a drag
      // ever actually begins.
      this.scrollTarget = this.findScrollableAncestor(rowEl);
    };

    handleEl.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault(); // stop the browser starting a text selection drag
      start(e.clientX, e.clientY);

      const moveHandler = (ev: MouseEvent) => {
        if (!this.dragging) {
          // Only vertical movement counts toward starting a drag — a
          // purely (or mostly) horizontal wiggle never should, since
          // there's nothing horizontal to drag here.
          const dy = ev.clientY - this.startY;
          if (Math.abs(dy) < DRAG_THRESHOLD) return;
          this.beginDrag();
        }
        this.handleMove(ev.clientY);
      };
      const upHandler = () => {
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', upHandler);
        this.handleUp();
      };
      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', upHandler);
    });

    // Same touch-action:none + manual-scroll-drive rationale as
    // ListDragController.registerRow() — see there for the full
    // explanation of why a plain preventDefault() alone can't work here.
    // Scoped to the handle only — everywhere else on the row keeps its
    // default touch-action, so native scrolling isn't disrupted.
    handleEl.style.touchAction = 'none';

    handleEl.addEventListener('touchstart', (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      const touchId = touch.identifier;
      start(touch.clientX, touch.clientY);

      let mode: 'pending' | 'scrolling' | 'dragging' = 'pending';
      let lastY = touch.clientY;

      let longPressTimer: number | null = window.setTimeout(() => {
        longPressTimer = null;
        if (mode !== 'pending') return; // already resolved as a scroll
        mode = 'dragging';
        this.beginDrag();
      }, TOUCH_LONG_PRESS_MS);

      const moveHandler = (ev: TouchEvent) => {
        const t = Array.from(ev.touches).find(t => t.identifier === touchId);
        if (!t) return;

        if (mode === 'dragging') {
          ev.preventDefault();
          this.handleMove(t.clientY);
          return;
        }

        if (mode === 'pending') {
          const dx = t.clientX - this.startX;
          const dy = t.clientY - this.startY;
          if (Math.hypot(dx, dy) <= TOUCH_LONG_PRESS_SLOP) return; // not resolved yet
          if (longPressTimer !== null) {
            window.clearTimeout(longPressTimer);
            longPressTimer = null;
          }
          mode = 'scrolling';
        }

        // mode === 'scrolling': drive the scroll ourselves, since
        // touch-action: none stops the browser from doing it natively
        // (scoped to the handle itself — see scrollBy()'s doc comment
        // for why that's not necessarily scrollContainer.scrollTop).
        ev.preventDefault();
        this.scrollBy(lastY - t.clientY);
        lastY = t.clientY;
      };
      const endHandler = () => {
        if (longPressTimer !== null) {
          window.clearTimeout(longPressTimer);
          longPressTimer = null;
        }
        handleEl.removeEventListener('touchmove', moveHandler);
        handleEl.removeEventListener('touchend', endHandler);
        handleEl.removeEventListener('touchcancel', endHandler);
        if (mode === 'dragging') this.handleUp();
      };
      handleEl.addEventListener('touchmove', moveHandler, { passive: false });
      handleEl.addEventListener('touchend', endHandler);
      handleEl.addEventListener('touchcancel', endHandler);
    }, { passive: true });
  }

  // ─── Movement / drop resolution ────────────────────────────────

  private beginDrag(): void {
    this.dragging = true;
    this.beginDragVisuals();
    this.callbacks.onDragStateChange(true);
  }

  private handleMove(clientY: number): void {
    if (!this.draggedNodeId || !this.dragging) return;
    this.updateDraggedPosition(clientY);
    this.updateAutoScroll(clientY);
    this.resolveHover();
  }

  private handleUp(): void {
    if (this.dragging && this.draggedNodeId && this.pendingAnchor) {
      this.callbacks.onDrop(this.draggedNodeId, this.pendingAnchor.anchorNodeId, this.pendingAnchor.insertBefore);
    }
    this.cleanup();
  }

  private resolveHover(): void {
    const draggedNodeId = this.draggedNodeId;
    if (!draggedNodeId || !this.listContainer || !this.rowsByNodeId) return;

    const swap = this.resolveSwapAnchor();
    if (!swap) return; // nothing to swap with yet (e.g. a single-row list)
    const { anchorNodeId, insertBefore } = swap;

    const resolvedKey = `${anchorNodeId}:${insertBefore}`;
    this.pendingAnchor = { anchorNodeId, insertBefore };
    if (resolvedKey === this.lastResolvedKey) return; // nothing changed, skip the reflow
    this.lastResolvedKey = resolvedKey;

    const { order, cascadedIds } = this.callbacks.previewOrder(draggedNodeId, anchorNodeId, insertBefore);
    this.lastOrder = order;

    // Attach/detach cascaded dependencies: lift whatever the cascade
    // newly pulled in, release whatever it no longer does (the dragged
    // node itself is never released).
    const newLiftedIds = new Set([draggedNodeId, ...cascadedIds]);
    for (const id of Array.from(this.liftedNodeIds)) {
      if (id !== draggedNodeId && !newLiftedIds.has(id)) this.releaseNode(id);
    }
    for (const id of newLiftedIds) {
      if (!this.liftedNodeIds.has(id)) this.liftNode(id);
    }
    this.updatePlaceholderHeight();

    this.applyPreviewOrder(order);
    this.layoutLiftedStack();
  }

  /**
   * Swap with whichever neighbor currently sits right before/after the
   * placeholder in flow, the moment the floating group's LEADING edge
   * on that side — groupTop for whatever's above, groupBottom for
   * whatever's below, including any attached cascaded dependencies —
   * crosses that neighbor's own midpoint. Using the group's outer edge
   * rather than its center means a group that just grew (picked up a
   * dependency) doesn't need to be centered over the next row to swap
   * with it; using the TARGET's midpoint rather than its edge is what
   * keeps this flicker-free: after a swap, the group's edge (unchanged
   * — no new pointer movement yet) sits exactly on the OLD neighbor's
   * midpoint, which is always short of that same neighbor's now-flush-
   * adjacent new position's OWN opposite-direction midpoint by about
   * half a row — nowhere near enough to immediately re-trigger the
   * reverse swap the way an edge-exact (or even edge-plus-small-margin)
   * check would. Already-lifted neighbors are skipped over — see
   * findAdjacentRow()'s own doc comment for why a lifted row's DOM
   * element can end up sitting right next to the placeholder despite
   * being out of flow.
   */
  private resolveSwapAnchor(): { anchorNodeId: string | null; insertBefore: boolean } | null {
    if (!this.placeholderEl) return null;

    const prevEl = this.findAdjacentRow('previousElementSibling');
    const prevId = prevEl?.dataset.nodeId;
    if (prevId) {
      const rect = this.settledRect.get(prevId);
      if (rect && this.groupTop <= (rect.top + rect.bottom) / 2) {
        return { anchorNodeId: prevId, insertBefore: true };
      }
    }

    const nextEl = this.findAdjacentRow('nextElementSibling');
    const nextId = nextEl?.dataset.nodeId;
    if (nextId) {
      const rect = this.settledRect.get(nextId);
      if (rect && this.groupBottom >= (rect.top + rect.bottom) / 2) {
        return { anchorNodeId: nextId, insertBefore: false };
      }
    }

    // Neither neighbor's threshold is crossed — keep whatever anchor
    // was last resolved (nothing to swap with right now).
    return this.pendingAnchor;
  }

  /**
   * Walks from the placeholder in the given sibling direction, skipping
   * past any row that's currently lifted. A lifted node's row elements
   * are never actually removed from the DOM tree (liftNode() only ever
   * changes their CSS position to fixed) — they just stay wherever they
   * originally sat — so the placeholder's OWN immediate sibling is
   * often one of those stale, no-longer-in-flow elements rather than
   * the real next in-flow row. Most visibly: right at drag start, the
   * placeholder is inserted immediately before the dragged row itself,
   * so its very first nextElementSibling IS the (just-lifted) dragged
   * row — without skipping past it, a downward swap could never be
   * found until some other reorder happened to physically move the
   * placeholder away from it first.
   */
  private findAdjacentRow(direction: 'previousElementSibling' | 'nextElementSibling'): HTMLElement | null {
    let el = this.placeholderEl?.[direction] as HTMLElement | null;
    while (el) {
      const id = el.dataset.nodeId;
      if (id && !this.liftedNodeIds.has(id)) return el;
      el = el[direction] as HTMLElement | null;
    }
    return null;
  }

  // ─── Visuals ────────────────────────────────────────────────────

  private beginDragVisuals(): void {
    if (!this.draggedRowEl?.parentElement || !this.draggedNodeId) return;
    document.body.style.userSelect = 'none';

    const container = this.draggedRowEl.parentElement;
    this.listContainer = container;

    const rows = Array.from(container.querySelectorAll<HTMLElement>('.preparation-step-row'));
    const byNode = new Map<string, HTMLElement[]>();
    for (const row of rows) {
      const id = row.dataset.nodeId;
      if (!id) continue;
      let group = byNode.get(id);
      if (!group) { group = []; byNode.set(id, group); }
      group.push(row);
    }
    this.rowsByNodeId = byNode;
    this.beforeRowsAnchor = (rows[0]?.previousElementSibling as HTMLElement | null) ?? null;

    // Baseline settledRect for everyone — nothing's moved yet, so a
    // plain read is safe here (see settledRect's own doc comment).
    for (const [nodeId, group] of byNode) {
      this.settledRect.set(nodeId, {
        top: group[0].getBoundingClientRect().top,
        bottom: group[group.length - 1].getBoundingClientRect().bottom,
      });
    }

    const draggedRows = byNode.get(this.draggedNodeId);
    if (!draggedRows || draggedRows.length === 0) return;
    this.currentDeltaY = 0;

    const placeholder = document.createElement('div');
    placeholder.style.boxSizing = 'border-box';
    placeholder.style.border = '2px dashed var(--accent)';
    placeholder.style.borderRadius = '4px';
    placeholder.style.background = 'var(--accent-alpha-13)';
    placeholder.style.pointerEvents = 'none';
    draggedRows[0].insertAdjacentElement('beforebegin', placeholder);
    this.placeholderEl = placeholder;

    this.liftNode(this.draggedNodeId);
    this.updatePlaceholderHeight();
    this.layoutLiftedStack();
  }

  /**
   * Lift `nodeId`'s rows out of flow (position: fixed, attached to the
   * same floating group as the dragged node) — called once for the
   * dragged node itself at drag start, and again, mid-drag, for every
   * dependency the live cascade starts pulling along.
   */
  private liftNode(nodeId: string): void {
    if (!this.rowsByNodeId || this.liftedNodeIds.has(nodeId)) return;
    const rows = this.rowsByNodeId.get(nodeId);
    if (!rows || rows.length === 0) return;

    // Settle any FLIP transition already in progress before measuring,
    // so the captured rect is the row's real resting position, not a
    // mid-animation one.
    for (const row of rows) {
      row.style.transition = 'none';
      row.style.transform = '';
    }
    const rects = rows.map(row => row.getBoundingClientRect());
    const blockTop = rects[0].top;
    const blockHeight = rects[rects.length - 1].bottom - blockTop;
    const rowTopOffsets = rects.map(r => r.top - blockTop);

    rows.forEach((row, i) => {
      row.style.position = 'fixed';
      row.style.left = `${rects[i].left}px`;
      row.style.width = `${rects[i].width}px`;
      row.style.top = `${rects[i].top}px`;
      row.style.zIndex = '10';
      row.style.boxShadow = '0 4px 14px rgba(0, 0, 0, 0.45)';
      row.style.background = 'var(--accent-alpha-13)';
      row.style.pointerEvents = 'none';
    });

    this.liftedBlocks.set(nodeId, { rows, rowTopOffsets, blockHeight });
    this.liftedNodeIds.add(nodeId);
    if (nodeId === this.draggedNodeId) this.draggedBlockOriginalTop = blockTop;
  }

  /** Returns `nodeId`'s rows to normal flow — the mirror of liftNode(). Never called for the dragged node itself. */
  private releaseNode(nodeId: string): void {
    const block = this.liftedBlocks.get(nodeId);
    if (!block) return;
    for (const row of block.rows) {
      row.style.position = '';
      row.style.left = '';
      row.style.width = '';
      row.style.top = '';
      row.style.zIndex = '';
      row.style.boxShadow = '';
      row.style.background = '';
      row.style.pointerEvents = '';
      row.style.transition = '';
      row.style.transform = '';
    }
    this.liftedBlocks.delete(nodeId);
    this.liftedNodeIds.delete(nodeId);
  }

  private updatePlaceholderHeight(): void {
    if (!this.placeholderEl) return;
    let total = 0;
    for (const block of this.liftedBlocks.values()) total += block.blockHeight;
    this.placeholderEl.style.height = `${total}px`;
  }

  /**
   * Positions every currently-lifted row: the dragged node's own block
   * tracks the pointer 1:1 (top = its original top + currentDeltaY, no
   * snapping), and every attached cascaded dependency stacks rigidly
   * above or below it — whichever side it falls on in lastOrder —
   * using each block's own frozen height, so the whole group visibly
   * moves together as one unit.
   */
  private layoutLiftedStack(): void {
    const draggedNodeId = this.draggedNodeId;
    if (!draggedNodeId) return;
    const draggedBlock = this.liftedBlocks.get(draggedNodeId);
    if (!draggedBlock) return;

    const draggedBlockTop = this.draggedBlockOriginalTop + this.currentDeltaY;
    draggedBlock.rows.forEach((row, i) => {
      row.style.top = `${draggedBlockTop + draggedBlock.rowTopOffsets[i]}px`;
    });

    const liftedSequence = (this.lastOrder.length > 0 ? this.lastOrder : [draggedNodeId])
      .filter(id => this.liftedNodeIds.has(id));
    const draggedIdx = liftedSequence.indexOf(draggedNodeId);
    if (draggedIdx === -1) {
      this.groupTop = draggedBlockTop;
      this.groupBottom = draggedBlockTop + draggedBlock.blockHeight;
      return;
    }

    // While stacking, also track the group's actual current top/bottom
    // edge (topmost/bottommost attached block) — resolveHover() reads
    // these fresh on its very next call, see groupTop's doc comment.
    let top = draggedBlockTop;
    this.groupTop = draggedBlockTop;
    for (let i = draggedIdx - 1; i >= 0; i--) {
      const block = this.liftedBlocks.get(liftedSequence[i]);
      if (!block) continue;
      top -= block.blockHeight;
      block.rows.forEach((row, j) => { row.style.top = `${top + block.rowTopOffsets[j]}px`; });
      this.groupTop = top;
    }

    top = draggedBlockTop + draggedBlock.blockHeight;
    this.groupBottom = top;
    for (let i = draggedIdx + 1; i < liftedSequence.length; i++) {
      const block = this.liftedBlocks.get(liftedSequence[i]);
      if (!block) continue;
      block.rows.forEach((row, j) => { row.style.top = `${top + block.rowTopOffsets[j]}px`; });
      top += block.blockHeight;
      this.groupBottom = top;
    }
  }

  /** Pins the dragged block to the pointer, 1:1, every move — see draggedBlockOriginalTop's doc comment. */
  private updateDraggedPosition(clientY: number): void {
    if (!this.draggedNodeId || !this.liftedBlocks.has(this.draggedNodeId)) return;
    this.currentDeltaY = clientY - this.startY;
    this.layoutLiftedStack();
  }

  /**
   * Reorder the real, still-in-flow rows to match `nodeOrder`
   * (RecipeStore.previewStepOrderCascaded()'s `order` — every node in
   * the recipe, not just the rendered-as-steps ones; ids with no row
   * here, e.g. ingredient nodes, are simply skipped), animating the
   * shift via a classic FLIP transform. The whole lifted group (dragged
   * node + any attached cascaded dependencies — always contiguous in
   * nodeOrder, per stepOrder.ts's own cascade behaviour) shares a
   * single placeholder slot here; the lifted rows themselves sit out of
   * this entirely, positioned instead by layoutLiftedStack().
   */
  private applyPreviewOrder(nodeOrder: string[]): void {
    if (!this.listContainer || !this.rowsByNodeId || !this.placeholderEl) return;

    const allRows: HTMLElement[] = [this.placeholderEl];
    const rowNodeId = new Map<HTMLElement, string>();
    for (const [nodeId, group] of this.rowsByNodeId) {
      if (this.liftedNodeIds.has(nodeId)) continue;
      for (const row of group) {
        allRows.push(row);
        rowNodeId.set(row, nodeId);
      }
    }

    // Cancel any FLIP transition still in flight on these rows BEFORE
    // reading anything — reorders routinely land well within
    // FLIP_DURATION_MS of each other while actively dragging, and
    // getBoundingClientRect() reflects whatever's currently PAINTED
    // (the interpolating, mid-transition value), not the row's true
    // resting position. Snapping first guarantees both oldRects below
    // and the settledRect update at the end are clean reads.
    for (const row of allRows) {
      row.style.transition = 'none';
      row.style.transform = '';
    }

    const oldRects = new Map<HTMLElement, DOMRect>();
    for (const row of allRows) oldRects.set(row, row.getBoundingClientRect());

    let anchor: HTMLElement | null = this.beforeRowsAnchor;
    let placeholderPlaced = false;
    for (const nodeId of nodeOrder) {
      if (this.liftedNodeIds.has(nodeId)) {
        if (!placeholderPlaced) {
          if (anchor) anchor.insertAdjacentElement('afterend', this.placeholderEl);
          else this.listContainer.insertBefore(this.placeholderEl, this.listContainer.firstChild);
          anchor = this.placeholderEl;
          placeholderPlaced = true;
        }
        continue; // the rest of the lifted group shares this same slot
      }
      const group = this.rowsByNodeId.get(nodeId);
      if (!group) continue; // not rendered in this list
      for (const row of group) {
        if (anchor) anchor.insertAdjacentElement('afterend', row);
        else this.listContainer.insertBefore(row, this.listContainer.firstChild);
        anchor = row;
      }
    }

    const nodeNewRect = new Map<string, { top: number; bottom: number }>();
    for (const row of allRows) {
      const oldRect = oldRects.get(row);
      if (!oldRect) continue;
      const newRect = row.getBoundingClientRect(); // clean — no transform pending at this point
      const deltaY = oldRect.top - newRect.top;
      if (Math.abs(deltaY) >= 0.5) {
        row.style.transform = `translateY(${deltaY}px)`;
        row.offsetHeight; // force a reflow so the transition below actually animates
        row.style.transition = `transform ${FLIP_DURATION_MS}ms ease`;
        row.style.transform = '';
      }

      const nodeId = rowNodeId.get(row);
      if (!nodeId) continue; // the placeholder itself — not tracked in settledRect
      const existing = nodeNewRect.get(nodeId);
      nodeNewRect.set(nodeId, existing
        ? { top: Math.min(existing.top, newRect.top), bottom: Math.max(existing.bottom, newRect.bottom) }
        : { top: newRect.top, bottom: newRect.bottom });
    }
    for (const [nodeId, rect] of nodeNewRect) this.settledRect.set(nodeId, rect);
  }

  // ─── Auto-scroll ────────────────────────────────────────────────

  /**
   * Walks up from `el` looking for the nearest ancestor that actually
   * scrolls (computed overflow-y of auto/scroll AND scrollHeight >
   * clientHeight) — not a guess based on which container the row
   * happens to be rendered into. On this app's narrow/mobile layout,
   * the Preparació panel's own container has no overflow at all; `html`
   * and `body` are locked to `overflow:hidden` (see input.css's
   * `narrow-fill-page` rule) and a DIFFERENT, higher ancestor
   * (`#home-elaboracio-panel`) is the one with the real `overflow-y:
   * auto` — so neither "scroll the passed-in container" nor "scroll the
   * window" alone works on narrow screens. On the wide layout there's
   * no such lock, so this walk reaches `<body>` with nothing scrollable
   * found and correctly falls back to `window`.
   */
  private findScrollableAncestor(el: HTMLElement): HTMLElement | Window {
    let node: HTMLElement | null = el.parentElement;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
        return node;
      }
      node = node.parentElement;
    }
    return window;
  }

  private scrollBy(deltaY: number): void {
    if (!this.scrollTarget) return;
    if (this.scrollTarget === window) {
      window.scrollBy(0, deltaY);
    } else {
      (this.scrollTarget as HTMLElement).scrollTop += deltaY;
    }
  }

  /**
   * Whether auto-scrolling further UP is still allowed — stops once
   * the panel's own header (beforeRowsAnchor, e.g. "Preparació") would
   * scroll past the top of the scrollable area, so the header stays
   * visible (you always still know which section you're in) rather
   * than scrolling away entirely. No such limit going down — there's
   * no equivalent trailing landmark worth pinning.
   */
  private headerAllowsScrollUp(): boolean {
    if (!this.beforeRowsAnchor || !this.scrollTarget) return true;
    const headerTop = this.beforeRowsAnchor.getBoundingClientRect().top;
    const limit = this.scrollTarget === window ? 0 : (this.scrollTarget as HTMLElement).getBoundingClientRect().top;
    // Scrolling up moves the header DOWN on screen (its rect.top rises
    // toward, then past, the boundary) — typically starting well below
    // zero, since dragging a row usually means it's already scrolled
    // past the header. Keep allowing it while the header hasn't yet
    // reached the boundary; stop once it has, or scrolling further
    // would push it past the top and reveal whatever precedes it.
    return headerTop < limit - 1;
  }

  private updateAutoScroll(clientY: number): void {
    if (!this.scrollTarget) return;
    // Near the edge of whatever's actually constraining the scroll —
    // the found ancestor's own bounds, or the viewport's if it's
    // `window` (there's no meaningful "rect" for that case, and the
    // viewport IS the relevant bound).
    const isWindow = this.scrollTarget === window;
    const top = isWindow ? 0 : (this.scrollTarget as HTMLElement).getBoundingClientRect().top;
    const bottom = isWindow ? window.innerHeight : (this.scrollTarget as HTMLElement).getBoundingClientRect().bottom;
    if (clientY < top + AUTOSCROLL_MARGIN) {
      this.autoScrollDir = this.headerAllowsScrollUp() ? -1 : 0;
    } else if (clientY > bottom - AUTOSCROLL_MARGIN) {
      this.autoScrollDir = 1;
    } else {
      this.autoScrollDir = 0;
    }

    if (this.autoScrollDir !== 0 && this.autoScrollRAF === null) {
      const step = () => {
        if (this.autoScrollDir === 0 || !this.dragging || !this.scrollTarget) {
          this.autoScrollRAF = null;
          return;
        }
        if (this.autoScrollDir === -1 && !this.headerAllowsScrollUp()) {
          this.autoScrollRAF = null;
          return;
        }
        this.scrollBy(this.autoScrollDir * AUTOSCROLL_SPEED);
        this.autoScrollRAF = requestAnimationFrame(step);
      };
      this.autoScrollRAF = requestAnimationFrame(step);
    } else if (this.autoScrollDir === 0 && this.autoScrollRAF !== null) {
      cancelAnimationFrame(this.autoScrollRAF);
      this.autoScrollRAF = null;
    }
  }

  // ─── Cleanup ────────────────────────────────────────────────────

  private cleanup(): void {
    const wasDragging = this.dragging;

    document.body.style.userSelect = '';

    // Relocate every lifted node's REAL rows to replace the placeholder
    // before removing it. Their own DOM position was never touched
    // during the drag — applyPreviewOrder() always routes the lifted
    // group through the placeholder instead of moving them — so left
    // alone they'd reappear wherever they happened to be sitting from
    // BEFORE the gesture even started. Once enough reordering has
    // shuffled other rows away from around that stale spot (even while
    // ultimately ending the drag back at the original position), that
    // can be anywhere, e.g. the very bottom — visibly "snapping to the
    // wrong place" on drop even though the move itself was a no-op.
    if (this.placeholderEl?.parentElement && this.draggedNodeId) {
      const liftedSequence = (this.lastOrder.length > 0 ? this.lastOrder : [this.draggedNodeId])
        .filter(id => this.liftedNodeIds.has(id));
      let anchor: HTMLElement = this.placeholderEl;
      for (const id of liftedSequence) {
        const block = this.liftedBlocks.get(id);
        if (!block) continue;
        for (const row of block.rows) {
          anchor.insertAdjacentElement('afterend', row);
          anchor = row;
        }
      }
    }
    this.placeholderEl?.remove();
    this.placeholderEl = null;
    if (this.rowsByNodeId) {
      for (const group of this.rowsByNodeId.values()) {
        for (const row of group) {
          row.style.transition = '';
          row.style.transform = '';
          row.style.position = '';
          row.style.left = '';
          row.style.width = '';
          row.style.top = '';
          row.style.zIndex = '';
          row.style.boxShadow = '';
          row.style.background = '';
          row.style.pointerEvents = '';
        }
      }
    }

    if (this.autoScrollRAF !== null) {
      cancelAnimationFrame(this.autoScrollRAF);
      this.autoScrollRAF = null;
    }
    this.autoScrollDir = 0;

    this.dragging = false;
    this.draggedNodeId = null;
    this.draggedRowEl = null;
    this.scrollTarget = null;
    this.listContainer = null;
    this.rowsByNodeId = null;
    this.beforeRowsAnchor = null;
    this.liftedBlocks.clear();
    this.liftedNodeIds.clear();
    this.settledRect.clear();
    this.draggedBlockOriginalTop = 0;
    this.groupTop = 0;
    this.groupBottom = 0;
    this.currentDeltaY = 0;
    this.lastOrder = [];
    this.pendingAnchor = null;
    this.lastResolvedKey = null;

    if (wasDragging) this.callbacks.onDragStateChange(false);
  }
}
